import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import { DASH } from '@/globals';
import formatNumber from '@/utils/NumberFormatter';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

export default class ReportProgress extends Validatable {
  statusCode: string;

  progress: number;

  executionStartedTimeUtc: string;

  executionFinishedTimeUtc: string;

  get status(): string {
    switch (this.statusCode) {
      case 'ERROR':
        return capitalize(i18n.global.t('names.error', { n: 1 }));
      case 'DONE':
        return capitalize(i18n.global.t('states.done'));
      case 'PENDING':
        return capitalize(i18n.global.t('states.pending'));
      case 'IN_PROGRESS':
        return capitalize(i18n.global.t('states.inProgress'));
      default:
        return capitalize(i18n.global.t('states.unknown'));
    }
  }

  get isRunning(): boolean {
    return this.statusCode !== 'DONE' && this.statusCode !== 'ERROR';
  }

  get isSuccess(): boolean {
    return this.statusCode === 'DONE';
  }

  get isError(): boolean {
    return this.statusCode === 'ERROR';
  }

  get startedOnFormatted(): string {
    if (this.executionStartedTimeUtc !== null) {
      return DateTime.fromISO(this.executionStartedTimeUtc, { zone: 'utc' })
        .toLocal()
        .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);
    }
    return DASH;
  }

  get finishedOnFormatted(): string {
    if (this.executionFinishedTimeUtc !== null) {
      return DateTime.fromISO(this.executionFinishedTimeUtc, { zone: 'utc' })
        .toLocal()
        .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);
    }
    return DASH;
  }

  get durationInSeconds(): number {
    if (this.executionStartedTimeUtc && this.executionFinishedTimeUtc) {
      const { seconds } = DateTime.fromISO(this.executionFinishedTimeUtc, { zone: 'utc' })
        .diff(DateTime.fromISO(this.executionStartedTimeUtc, { zone: 'utc' }), 'seconds')
        .toObject();
      return seconds === 0 ? 1 : seconds;
    }
    return null;
  }

  get durationInSecondsFormatted(): string {
    if (this.durationInSeconds !== null) {
      return `${this.durationInSeconds} ${i18n.global.t('names.second', Math.floor(this.durationInSeconds))}`;
    }
    return DASH;
  }

  get progressFormatted(): string {
    return formatNumber(this.progress, '0%');
  }
}
